import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, FIREARM_STORAGE_LOCATION_TYPE, FirearmStorageLocation, RefCode } from 'assets-model';
import { BusinessUnitService, FirearmStorageLocationDataSource, FirearmStorageLocationService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class FirearmStorageLocationSearchComponentBase extends CrudListComponent<FirearmStorageLocation, number> {
  protected systemConfig: any;

  displayedColumns: string[] = [ 'firearmStorageLocationId', 'businessUnit.buId', 'addr1', 'addr2', 'addr3', 'county', 'createdBy', 'createdDate', 'description', 'disposalLocation', 'email', 'location', 'locationType', 'lockVersion', 'postcode', 'town', 'updatedBy', 'updatedDate', 'actions'];

  public searchForm: FormGroup;

  firearmStorageLocationIdFilter: number;
  @ViewChild('firearmStorageLocationIdFilterInput') firearmStorageLocationIdFilterInput: ElementRef;

  businessUnitFilter: BusinessUnit;
  businessUnitSelectionList: BusinessUnit[];
  @ViewChild('businessUnitFilterInput') businessUnitFilterInput: MatSelect;

  addr1Filter: string;
  @ViewChild('addr1FilterInput') addr1FilterInput: ElementRef;

  addr2Filter: string;
  @ViewChild('addr2FilterInput') addr2FilterInput: ElementRef;

  addr3Filter: string;
  @ViewChild('addr3FilterInput') addr3FilterInput: ElementRef;

  countyFilter: string;
  @ViewChild('countyFilterInput') countyFilterInput: ElementRef;

  createdByFilter: string;
  @ViewChild('createdByFilterInput') createdByFilterInput: ElementRef;

  createdDateFilter: Date;
  @ViewChild('createdDateFilterInput') createdDateFilterInput: ElementRef;
  createdDateLessThanFilter: Date;
  @ViewChild('createdDateLessThanFilterInput') createdDateLessThanFilterInput: ElementRef;
  createdDateLessThanOrEqualToFilter: Date;
  @ViewChild('createdDateLessThanOrEqualToFilterInput') createdDateLessThanOrEqualToFilterInput: ElementRef;
  createdDateGreaterThanFilter: Date;
  @ViewChild('createdDateGreaterThanFilterInput') createdDateGreaterThanFilterInput: ElementRef;
  createdDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('createdDateGreaterThanOrEqualToFilterInput') createdDateGreaterThanOrEqualToFilterInput: ElementRef;

  descriptionFilter: string;
  @ViewChild('descriptionFilterInput') descriptionFilterInput: ElementRef;

  disposalLocationFilter: boolean;
  @ViewChild('disposalLocationFilterInput') disposalLocationFilterInput: MatSelect;

  emailFilter: string;
  @ViewChild('emailFilterInput') emailFilterInput: ElementRef;

  locationFilter: string;
  @ViewChild('locationFilterInput') locationFilterInput: ElementRef;

  locationTypeSelectionList: RefCode[];
  locationTypeFilter: string;
  @ViewChild('locationTypeFilterInput') locationTypeFilterInput: MatSelect;

  postcodeFilter: string;
  @ViewChild('postcodeFilterInput') postcodeFilterInput: ElementRef;

  townFilter: string;
  @ViewChild('townFilterInput') townFilterInput: ElementRef;

  updatedByFilter: string;
  @ViewChild('updatedByFilterInput') updatedByFilterInput: ElementRef;

  updatedDateFilter: Date;
  @ViewChild('updatedDateFilterInput') updatedDateFilterInput: ElementRef;
  updatedDateLessThanFilter: Date;
  @ViewChild('updatedDateLessThanFilterInput') updatedDateLessThanFilterInput: ElementRef;
  updatedDateLessThanOrEqualToFilter: Date;
  @ViewChild('updatedDateLessThanOrEqualToFilterInput') updatedDateLessThanOrEqualToFilterInput: ElementRef;
  updatedDateGreaterThanFilter: Date;
  @ViewChild('updatedDateGreaterThanFilterInput') updatedDateGreaterThanFilterInput: ElementRef;
  updatedDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('updatedDateGreaterThanOrEqualToFilterInput') updatedDateGreaterThanOrEqualToFilterInput: ElementRef;


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected firearmStorageLocationService: FirearmStorageLocationService,
    protected businessUnitService: BusinessUnitService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(FIREARM_STORAGE_LOCATION_TYPE, route, router, userStorage, dialog, firearmStorageLocationService, injector);

    this.searchForm = this.fb.group({
      firearmStorageLocationId: [null],
      businessUnit: [null],
      addr1: [null],
      addr2: [null],
      addr3: [null],
      county: [null],
      createdBy: [null],
      createdDateLessThan: [null],
      createdDateLessThanOrEqualTo: [null],
      createdDateGreaterThan: [null],
      createdDateGreaterThanOrEqualTo: [null],
      createdDate: [null],
      description: [null],
      disposalLocation: [null],
      email: [null],
      location: [null],
      locationType: [null],
      postcode: [null],
      town: [null],
      updatedBy: [null],
      updatedDateLessThan: [null],
      updatedDateLessThanOrEqualTo: [null],
      updatedDateGreaterThan: [null],
      updatedDateGreaterThanOrEqualTo: [null],
      updatedDate: [null]
    });
  }

  createDataSource(): CrudDataSource<FirearmStorageLocation> {
    return new FirearmStorageLocationDataSource(this.firearmStorageLocationService);
  }

  createServiceArguments(): any {
    return {
      firearmStorageLocationId: this.firearmStorageLocationIdFilter,
      buId: this.businessUnitFilter && this.businessUnitFilter.buId,
      addr1: this.addr1Filter,
      addr2: this.addr2Filter,
      addr3: this.addr3Filter,
      county: this.countyFilter,
      createdBy: this.createdByFilter,
      createdDateLessThan: this.createdDateLessThanFilter,
      createdDateLessThanOrEqualTo: this.createdDateLessThanOrEqualToFilter,
      createdDateGreaterThan: this.createdDateGreaterThanFilter,
      createdDateGreaterThanOrEqualTo: this.createdDateGreaterThanOrEqualToFilter,
      createdDate: this.createdDateFilter,
      description: this.descriptionFilter,
      disposalLocation: this.disposalLocationFilter,
      email: this.emailFilter,
      location: this.locationFilter,
      locationType: this.locationTypeFilter,
      postcode: this.postcodeFilter,
      town: this.townFilter,
      updatedBy: this.updatedByFilter,
      updatedDateLessThan: this.updatedDateLessThanFilter,
      updatedDateLessThanOrEqualTo: this.updatedDateLessThanOrEqualToFilter,
      updatedDateGreaterThan: this.updatedDateGreaterThanFilter,
      updatedDateGreaterThanOrEqualTo: this.updatedDateGreaterThanOrEqualToFilter,
      updatedDate: this.updatedDateFilter
    };
  }
  
  clearFiltersBeforeLoadPage() {
    this.restoreFormFromParams(this.searchForm);
  }
  
  clearFilterVariables() {
    this.firearmStorageLocationIdFilter = null;
    this.businessUnitFilter = null;
    this.addr1Filter = null;
    this.addr2Filter = null;
    this.addr3Filter = null;
    this.countyFilter = null;
    this.createdByFilter = null;
    this.createdDateLessThanFilter = null;
    this.createdDateLessThanOrEqualToFilter = null;
    this.createdDateGreaterThanFilter = null;
    this.createdDateGreaterThanOrEqualToFilter = null;
    this.createdDateFilter = null;
    this.descriptionFilter = null;
    this.disposalLocationFilter = null;
    this.emailFilter = null;
    this.locationFilter = null;
    this.locationTypeFilter = null;
    this.postcodeFilter = null;
    this.townFilter = null;
    this.updatedByFilter = null;
    this.updatedDateLessThanFilter = null;
    this.updatedDateLessThanOrEqualToFilter = null;
    this.updatedDateGreaterThanFilter = null;
    this.updatedDateGreaterThanOrEqualToFilter = null;
    this.updatedDateFilter = null;
  }
  
  clearFilters() {
    this.clearFilterVariables();
    this.clearFiltersBeforeLoadPage();
    this.loadPage();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedInputEventListener(this.firearmStorageLocationIdFilterInput);
    this.addSearchOnChangedSelectEventListener(this.businessUnitFilterInput);
    this.addSearchOnChangedInputEventListener(this.addr1FilterInput);
    this.addSearchOnChangedInputEventListener(this.addr2FilterInput);
    this.addSearchOnChangedInputEventListener(this.addr3FilterInput);
    this.addSearchOnChangedInputEventListener(this.countyFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdByFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.createdDateFilterInput);
    this.addSearchOnChangedInputEventListener(this.descriptionFilterInput);
    this.addSearchOnChangedSelectEventListener(this.disposalLocationFilterInput);
    this.addSearchOnChangedInputEventListener(this.emailFilterInput);
    this.addSearchOnChangedInputEventListener(this.locationFilterInput);
    this.addSearchOnChangedSelectEventListener(this.locationTypeFilterInput);
    this.addSearchOnChangedInputEventListener(this.postcodeFilterInput);
    this.addSearchOnChangedInputEventListener(this.townFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedByFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.updatedDateFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  ngOnInit() {
    super.ngOnInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }

  protected initExportOptions(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initExportOptions(config);
  }
  
  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      firearmStorageLocationId: (value: number) => self.firearmStorageLocationIdFilter = value,
      businessUnit: (value: BusinessUnit) => { self.businessUnitFilter = value; this.businessUnitChanged(value, false); },
      addr1: (value: string) => self.addr1Filter = value,
      addr2: (value: string) => self.addr2Filter = value,
      addr3: (value: string) => self.addr3Filter = value,
      county: (value: string) => self.countyFilter = value,
      createdBy: (value: string) => self.createdByFilter = value,
      createdDateLessThan: (value: Date) => self.createdDateLessThanFilter = value,
      createdDateLessThanOrEqualTo: (value: Date) => self.createdDateLessThanOrEqualToFilter = value,
      createdDateGreaterThan: (value: Date) => self.createdDateGreaterThanFilter = value,
      createdDateGreaterThanOrEqualTo: (value: Date) => self.createdDateGreaterThanOrEqualToFilter = value,
      createdDate: (value: Date) => self.createdDateFilter = value,
      description: (value: string) => self.descriptionFilter = value,
      disposalLocation: (value: boolean) => self.disposalLocationFilter = value,
      email: (value: string) => self.emailFilter = value,
      location: (value: string) => self.locationFilter = value,
      locationType: (value: string) => self.locationTypeFilter = value,
      postcode: (value: string) => self.postcodeFilter = value,
      town: (value: string) => self.townFilter = value,
      updatedBy: (value: string) => self.updatedByFilter = value,
      updatedDateLessThan: (value: Date) => self.updatedDateLessThanFilter = value,
      updatedDateLessThanOrEqualTo: (value: Date) => self.updatedDateLessThanOrEqualToFilter = value,
      updatedDateGreaterThan: (value: Date) => self.updatedDateGreaterThanFilter = value,
      updatedDateGreaterThanOrEqualTo: (value: Date) => self.updatedDateGreaterThanOrEqualToFilter = value,
      updatedDate: (value: Date) => self.updatedDateFilter = value
    }
  }
  
  restoreFormChanges() {
    const self = this;

    // IMPORTANT that the restoreFormChanges call occurs before the initial search - typically best done in onInitAsyncComplete
    // Whereever a form control binds with [ngModel] to a reactive form, ngModel takes precedence,
    // and needs it's own setter function to set the scope attribute bound
    this.formStorage.restoreFormChanges(this.searchForm, this.transformAllPropertiesToModelSetters());
  }

  firearmStorageLocationIdChanging(value: number, unsetDependents: boolean = true) {}
  
  firearmStorageLocationIdChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
      }
    );
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  

  addr1Changing(value: string, unsetDependents: boolean = true) {}
  
  addr1Changed(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  addr2Changing(value: string, unsetDependents: boolean = true) {}
  
  addr2Changed(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  addr3Changing(value: string, unsetDependents: boolean = true) {}
  
  addr3Changed(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  countyChanging(value: string, unsetDependents: boolean = true) {}
  
  countyChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  createdDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  descriptionChanging(value: string, unsetDependents: boolean = true) {}
  
  descriptionChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disposalLocationChanging(value: boolean, unsetDependents: boolean = true) {}
  
  disposalLocationChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  emailChanging(value: string, unsetDependents: boolean = true) {}
  
  emailChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  locationChanging(value: string, unsetDependents: boolean = true) {}
  
  locationChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  locationTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_STORAGE_LOCATION_TYPE').subscribe(
      data => {
        this.locationTypeSelectionList = data;
      }
    );
  }

  locationTypeChanging(value: string, unsetDependents: boolean = true) {}
  
  locationTypeChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  postcodeChanging(value: string, unsetDependents: boolean = true) {}
  
  postcodeChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  townChanging(value: string, unsetDependents: boolean = true) {}
  
  townChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  updatedDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disabledTooltip(element) {
    if (!element) {
      return true;
    }
    return element.valid || !element.touched;
  }

  get firearmStorageLocationId() {
    return this.searchForm && this.searchForm.get('firearmStorageLocationId');
  }

  get businessUnit() {
    return this.searchForm && this.searchForm.get('businessUnit');
  }

  get addr1() {
    return this.searchForm && this.searchForm.get('addr1');
  }

  get addr2() {
    return this.searchForm && this.searchForm.get('addr2');
  }

  get addr3() {
    return this.searchForm && this.searchForm.get('addr3');
  }

  get county() {
    return this.searchForm && this.searchForm.get('county');
  }

  get createdBy() {
    return this.searchForm && this.searchForm.get('createdBy');
  }

  get createdDate() {
    return this.searchForm && this.searchForm.get('createdDate');
  }

  get createdDateLessThan() {
    return this.searchForm && this.searchForm.get('createdDateLessThan');
  }
  
  get createdDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('createdDateLessThanOrEqualTo');
  }
  
  get createdDateGreaterThan() {
    return this.searchForm && this.searchForm.get('createdDateGreaterThan');
  }
  
  get createdDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('createdDateGreaterThanOrEqualTo');
  }

  get description() {
    return this.searchForm && this.searchForm.get('description');
  }

  get disposalLocation() {
    return this.searchForm && this.searchForm.get('disposalLocation');
  }

  get email() {
    return this.searchForm && this.searchForm.get('email');
  }

  get location() {
    return this.searchForm && this.searchForm.get('location');
  }

  get locationType() {
    return this.searchForm && this.searchForm.get('locationType');
  }

  get postcode() {
    return this.searchForm && this.searchForm.get('postcode');
  }

  get town() {
    return this.searchForm && this.searchForm.get('town');
  }

  get updatedBy() {
    return this.searchForm && this.searchForm.get('updatedBy');
  }

  get updatedDate() {
    return this.searchForm && this.searchForm.get('updatedDate');
  }

  get updatedDateLessThan() {
    return this.searchForm && this.searchForm.get('updatedDateLessThan');
  }
  
  get updatedDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('updatedDateLessThanOrEqualTo');
  }
  
  get updatedDateGreaterThan() {
    return this.searchForm && this.searchForm.get('updatedDateGreaterThan');
  }
  
  get updatedDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('updatedDateGreaterThanOrEqualTo');
  }


}



